import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Navigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { PageRef } from 'app/catalog/product/page';
import { ScrollToTop } from 'components/Common/ScrollToTop/ScrollToTop';
import Loading from 'components/Common/Loading/Loading';
import ProductCarousel from 'components/Common/Carousel/ProductsCarousel/ProductCarousel/ProductCarousel';
import ProductHeader from './ProductHeader/ProductHeader';
import ProductJsonLd from './ProductJsonLd/ProductJsonLd';
import ProductDetails from './ProductDetails/ProductDetails';
import ProductGallery from './ProductGallery/ProductGallery';
import ProductReassurance from './ProductReassurance/ProductReassurance';
import ProductTabDesktop from './ProductTab/ProductTabDesktop';
import ProductTabMobile from './ProductTab/ProductTabMobile';
import SimilarProductsCarousel from './Carousels/SimilarProductsCarousel';
import RecentlySeenCarousel from './Carousels/RecentlySeenCarousel';
import ProductsCarousel from 'components/Common/Carousel/ProductsCarousel/ProductsCarousel';
import LinkedProductsCarousel from 'components/Common/Carousel/LinkedProductsCarousel/LinkedProductsCarousel';
import { ErrorBoundary } from 'components/Common/Errors/ErrorBoundary/ErrorBoundary';
import PageNotFound from 'components/Common/Errors/PageNotFound/PageNotFound';
import CTATagScript from 'components/Veesual/CTATagScript';
import useLargeSizeMode from 'hooks/ProductPageV2/useLargeSizeMode';
import useProductData from 'hooks/ProductPageV2/useProductData';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import { useProductReviewsDetails } from 'hooks/ProductPageV2/ProductReviews/useProductReviewsDetails';
import { useProductRating } from 'hooks/ProductPageV2/ProductReviews/useProductRating';
import useEnv from 'hooks/useEnv';
import TabProvider from 'providers/TabProvider';
import { getSlideType } from 'services/carousel';
import { getCarouselProductsData } from 'services/formatter';
import { getUri, getUriWithoutLocale } from 'services/generic';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { formatProductV2 } from 'services/gtm/gtmUtils';
import { getProductData } from 'services/gtm/gtmPageView';
import { IS_ENABLE as IS_VEESUAL_ENABLE } from 'services/mix-and-match';
import {
  extractProductSku,
  getVisuelFace,
  isLargeSize,
} from 'services/product';
import { TABLET_PORTRAIT_MAX } from 'settings/device';
import { GTM_PAGE_PRODUCT } from 'settings/gtm/gtmPageView';
import { MOBILE_AND_TABLET_PORTRAIT } from 'settings/mediaQuery';
import { Morphologies } from 'settings/productV2';
import { ProductDynamicAttributesType } from 'types/Product';
import { SelectedProductAttributes } from 'types/ProductPageV2/Products';
import {
  ProductReviewsDetailsType,
  ProductReviewsRatingType,
} from 'types/ProductPageV2/ProductReviews';
import { ConfigurableProductPage } from 'types/ProductPage/ProductPage';

import styles from './ProductPage.module.scss';

type Props = { data: ConfigurableProductPage };

/**
 * Product page of website. Contains details product data.
 */
const ProductPage = forwardRef<PageRef, Props>(function ProductPageContent(
  { data },
  ref
) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { country } = useEnv();
  const [color, setColor] = useState<string>();
  const {
    formattedProductData,
    formattedProductDynamicData,
    productType,
    isMCProduct,
    isLoaded,
  } = useProductData(data, color, setColor);

  const {
    product,
    productAttributes,
    variantsAttributes,
    colors,
    sizes,
    cups,
    breadcrumb,
  } = formattedProductData;

  const {
    seo,
    reassurance,
    offer,
    relatedProducts,
    similarProducts,
    linkedProducts,
    lastVisitedProducts,
  } = formattedProductDynamicData;

  const pageTitle =
    productAttributes?.fPTitle?.Value ||
    t('META_TITLE_TEMPLATE', {
      title: t('PRODUCT_HEAD_META_TITLE_PAGE', {
        title: productAttributes?.libelle?.Value,
        color: productAttributes?.couleur.Value,
      }),
    });

  useImperativeHandle(
    ref,
    () => {
      return {
        canChangeStaticPath: () => {
          const variantsSkus = data.variantsSkus;
          const productSku = extractProductSku(pathname);

          return !variantsSkus.some(
            variantsSku =>
              variantsSku.toLowerCase() === productSku.toLowerCase()
          );
        },
      };
    },
    [pathname, data]
  );

  useGtmPageView(GTM_PAGE_PRODUCT, pageTitle as string, {
    products: product && [formatProductV2(product.Attributes)],
    isLoaded,
  });

  const idReviewVerify = productAttributes?.idAvisverifie.Value;
  const { details: detailsReviews } = useProductReviewsDetails(idReviewVerify);
  const { rating } = useProductRating(idReviewVerify);
  const formattedGtmProductData = useMemo(
    () =>
      productAttributes
        ? getProductData(formatProductV2(productAttributes))
        : undefined,
    [productAttributes]
  );

  if (isLoaded && product && !variantsAttributes && seo) {
    if (getUriWithoutLocale(pathname) === seo.CanonicalUrl) {
      return <PageNotFound />;
    }
    // No variants are existing for current product
    return (
      <Navigate
        replace
        to={getUri(seo?.CanonicalUrl + window.location.search)}
      />
    );
  }

  if (
    !product ||
    !productAttributes ||
    !variantsAttributes ||
    !sizes ||
    !cups ||
    !colors ||
    !breadcrumb
  ) {
    return (
      <>
        <ScrollToTop />
        <Loading loading />
      </>
    );
  }

  const isShowLinkedProducts = linkedProducts?.length > 2;
  const isShowRelatedProducts = relatedProducts?.length > 3;
  const isShowSimilarProducts = similarProducts?.length > 6;
  const isShowRecentlySeen =
    !(isShowLinkedProducts && isShowRelatedProducts && isShowSimilarProducts) &&
    lastVisitedProducts?.length > 0;

  const renderHelmet = (): JSX.Element => {
    return (
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name={'description'}
          content={
            productAttributes?.fPMetaDescription?.Value ||
            t('DEFAULT_HEAD_META_DESCRIPTION')
          }
        />
        {seo?.CanonicalUrl && (
          <link
            rel={'canonical'}
            href={`${window.location.protocol}//${window.location.host}/${country}${seo.CanonicalUrl}`}
          />
        )}
      </Helmet>
    );
  };

  return (
    <>
      {renderHelmet()}
      {IS_VEESUAL_ENABLE && (
        <CTATagScript productAttributes={productAttributes} />
      )}
      <ProductHeader
        classes={{ container: styles.header }}
        breadcrumbs={breadcrumb}
        productAttributes={productAttributes}
      />
      <TabProvider key={productAttributes.idParent?.Value}>
        <ScrollToTop />
        <div className={styles.main}>
          <div className={styles.gallery}>
            {productAttributes && (
              <ProductGallery
                productAttributes={productAttributes}
                onSlideAction={direction => {
                  if (direction === 'next') {
                    pushGtmEvent({
                      event: '_click_other',
                      category: 'clic autre - fiche produit',
                      element: "flèche plus d'images",
                      products: formattedGtmProductData && [
                        formattedGtmProductData,
                      ],
                    });
                  }
                }}
                onSwitchMorphology={morphology => {
                  pushGtmEvent({
                    event: '_click_other',
                    category: 'clic autre - fiche produit',
                    element: 'modèle choisi',
                    detail:
                      morphology === Morphologies.CURVY ? 'curvy' : 'standard',
                    text:
                      morphology === Morphologies.CURVY
                        ? t('LABEL_SEE_IN_CURVY_SIZE')
                        : t('LABEL_SEE_IN_STANDARD_SIZE'),
                    products: formattedGtmProductData && [
                      formattedGtmProductData,
                    ],
                  });
                }}
              />
            )}
          </div>
          <div className={styles.details}>
            <ProductDetails
              product={product}
              productAttributes={productAttributes}
              skus={variantsAttributes}
              type={productType}
              colors={colors}
              sizes={sizes}
              cups={cups}
              color={color ?? productAttributes.couleur.Value}
              setColor={setColor}
              rating={rating}
              offer={offer}
              isMCProduct={isMCProduct}
            />
            {reassurance && <ProductReassurance htmlContent={reassurance} />}
          </div>
        </div>
        <ProductTabs
          productAttributes={productAttributes}
          detailsReviews={detailsReviews}
          rating={rating}
        />
        {isShowLinkedProducts && (
          <ErrorBoundary fallback={<></>}>
            <LinkedProductsCarouselWrapper
              products={linkedProducts}
              productAttributes={productAttributes}
            />
          </ErrorBoundary>
        )}
        {isShowRelatedProducts && (
          <ErrorBoundary fallback={<></>}>
            <RelatedProductsCarouselWrapper products={relatedProducts} />
          </ErrorBoundary>
        )}
        {isShowSimilarProducts && (
          <ErrorBoundary fallback={<></>}>
            <SimilarProductsCarouselWrapper products={similarProducts} />
          </ErrorBoundary>
        )}
        {isShowRecentlySeen && (
          <ErrorBoundary fallback={<></>}>
            <RecentlySeenCarouselWrapper products={lastVisitedProducts} />
          </ErrorBoundary>
        )}
        <ProductJsonLd
          product={product}
          rating={rating}
          reviews={detailsReviews}
        />
      </TabProvider>
    </>
  );
});

export default ProductPage;

function LinkedProductsCarouselWrapper({
  products,
  productAttributes,
}: {
  products: ProductDynamicAttributesType[];
  productAttributes: SelectedProductAttributes;
}): JSX.Element {
  const { t } = useTranslation();
  const { LSModeEnabled } = useLargeSizeMode();
  const preferCurvy = LSModeEnabled && isLargeSize(productAttributes);

  return (
    <LinkedProductsCarousel
      title={t('BLOCK_LINKED_PRODUCTS_TITLE')}
      productImg={getVisuelFace(productAttributes, preferCurvy)}
      products={getCarouselProductsData(products, LSModeEnabled)}
      classes={{
        wrapper: styles.linkedProductsContainer,
      }}
      onClickProduct={() => {
        scrollTo(0, 0);
      }}
    />
  );
}

type CarouselWrapperProps = {
  products: ProductDynamicAttributesType[];
};

function RelatedProductsCarouselWrapper({
  products,
}: CarouselWrapperProps): JSX.Element {
  const { t } = useTranslation();
  const { LSModeEnabled } = useLargeSizeMode();
  const productsLength = products.length;

  return (
    <ProductsCarousel
      title={t('LABEL_COMPLETE_YOUR_LOOK')}
      products={getCarouselProductsData(products, LSModeEnabled)}
      renderProduct={product => (
        <ProductCarousel
          classes={{ shortLabel: styles.shortLabel }}
          product={product}
        />
      )}
      splideOptions={{
        perPage: 4,
        type: getSlideType(4, productsLength),
        breakpoints: {
          [TABLET_PORTRAIT_MAX]: {
            perPage: 2,
            type: getSlideType(2, productsLength),
          },
        },
      }}
      classes={{
        wrapper: classNames(
          styles.carouselWrapper,
          styles.relatedProductsContainer
        ),
      }}
    />
  );
}

function SimilarProductsCarouselWrapper({
  products,
}: CarouselWrapperProps): JSX.Element {
  const { t } = useTranslation();
  const { LSModeEnabled } = useLargeSizeMode();

  return (
    <SimilarProductsCarousel
      title={t('LABEL_YOU_MAY_ALSO_LIKE')}
      products={getCarouselProductsData(products, LSModeEnabled)}
      classes={{
        wrapper: classNames(styles.carouselWrapper, styles.full),
        carouselContainer: styles.carouselContainer,
      }}
    />
  );
}

function RecentlySeenCarouselWrapper({
  products,
}: CarouselWrapperProps): JSX.Element {
  const { t } = useTranslation();
  const { LSModeEnabled } = useLargeSizeMode();

  return (
    <RecentlySeenCarousel
      title={t('LABEL_RECENTLY_VIEWED')}
      products={getCarouselProductsData(products, LSModeEnabled)}
      classes={{
        wrapper: classNames(styles.carouselWrapper, styles.full),
        carouselContainer: styles.carouselContainer,
      }}
    />
  );
}

function ProductTabs({
  productAttributes,
  rating,
  detailsReviews,
}: {
  productAttributes: SelectedProductAttributes;
  rating: ProductReviewsRatingType | undefined;
  detailsReviews: ProductReviewsDetailsType[] | undefined;
}) {
  const isMobileAndPortraitTablet = useMediaQuery({
    query: MOBILE_AND_TABLET_PORTRAIT,
  });

  return (
    <div className={styles.tabs}>
      {isMobileAndPortraitTablet && (
        <ProductTabMobile
          productAttributes={productAttributes}
          rating={rating}
          detailsReviews={detailsReviews}
        />
      )}
      {!isMobileAndPortraitTablet && (
        <ProductTabDesktop
          productAttributes={productAttributes}
          rating={rating}
          detailsReviews={detailsReviews}
        />
      )}
    </div>
  );
}
